/**
 * ScreenShell — sidebar + topbar + main column wrapper used by all /app/* screens.
 *
 * Phase 9 IA: sidebar restructured into two sections:
 *   1. Cross-trust — Overview, My trusts, Inbox, Security, Privacy
 *   2. Active trust — per-trust tabs (only when inside /app/trusts/$id/*)
 */

import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { displayLegalName } from '../../lib/format/legal-name';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { ReignMark } from '../reign-mark';
import { FirmLogo } from '../firm-logo';
import { FirmMonogram } from '../firm-monogram';
import { Icon } from '../icon';
import { Modal } from '../ui/modal';
import { TenantSwitcher } from '../header/tenant-switcher';
import type { IconName } from '../icon';
import { useOperatorMe, useTenantMe, useTrust, useTrusts } from '../../lib/api';
import { useActingContext } from '../../lib/acting/context';
import { useActiveBranding } from '../../lib/use-active-branding';
import { useSyncMyTenants } from '../../lib/use-sync-my-tenants';
import { resolveBrandLockup } from '../../lib/brand-lockup';
// NOTIF-01 / TRUSTEE-11: the notification + search hooks are imported DIRECTLY
// from their modules (NOT via the api barrel) per the shell batch's rule.
import {
  useNotifications,
  useMarkNotificationsRead,
  type NotificationItem,
  type NotificationKind,
} from '../../lib/api/notifications';
// B1: durable read-state is the SERVER cursor (`POST /v1/notifications/read`,
// surfaced per-item as `NotificationItem.read`). This store is only the local
// per-item "seen" overlay for opening a single row — see its header.
import {
  useNotificationsReadState,
  markNotificationRead,
} from '../../lib/notifications-read-store';
import { useTrustSubjectsSearch } from '../../lib/api/search';
import { displayPersonName } from '../../lib/format/person-name';
import { useSidebarScrollMemory } from '../../lib/use-sidebar-scroll-memory';
import { labelForRole } from '../../lib/labels';
import { useHiddenFromActingFirm } from '../../lib/authz/act-as-nav';
import { useToastStore, type ToastRecord } from '../../lib/toast-store';
import { signOutAndRedirect } from '../../lib/sign-out';
import { useTranslation, type TranslationKey } from '../../lib/i18n';
import { SkipToContentLink } from '../ui/skip-link';
// POR-05 — the portal's light/dark control. It shipped on 2026-09-15 into
// `dashboard.tsx`'s hand-rolled topbar, which meant exactly one portal screen
// had it: the dashboard. Folding that shell into this one (cohesion pass phase
// 3) is what gives the other seventeen portal screens the same control. The
// hook is shared with the firm console on purpose — an operator who is both
// should not have to choose twice.
import { useAdminTheme } from '../../lib/use-admin-theme';
import { initialsOf } from '../../lib/format/initials';

// H-81 (2026-05-20 — Henry persona walk): the sidebar foot rendered the
// raw `me.role` slug — for a settlor who has the Supabase `admin` role
// (because they administer their own B2C tenant) it surfaced as
// "Administrator", which is wrong in SA trust context. We now route
// through the centralised `labelForRole` dictionary and special-case
// the per-trust settlor with "Settlor & Trustee" so the label matches
// the user's actual role on the trust they're currently viewing.

// ── NOTIF-01: notification presentation helpers ───────────────────────────────

/** Icon per notification kind — reuses the existing design-system glyphs.
 *  `member-verification-pending` uses `shield`, the existing glyph for
 *  compliance/risk/identity-verification surfaces (risk-rating, security
 *  centre, entity-graph) — there is no dedicated FICA/identity icon. */
const NOTIF_ICON: Record<NotificationKind, IconName> = {
  'awaiting-signature': 'sign',
  'awaiting-acknowledgement': 'cash',
  'upcoming-meeting': 'team',
  'document-rejected': 'doc',
  'unread-message': 'msg',
  'member-verification-pending': 'shield',
};

/** Short, non-PII category label shown as the pill on each row. */
const NOTIF_PILL: Record<NotificationKind, string> = {
  'awaiting-signature': 'Signature',
  'awaiting-acknowledgement': 'Acknowledge',
  'upcoming-meeting': 'Meeting',
  'document-rejected': 'Document',
  'unread-message': 'Message',
  'member-verification-pending': 'Verification',
};

/**
 * Deep-link target for a notification. Each kind routes to the surface where
 * the user actions it, scoped to the item's trust. Returns a path string the
 * topbar renders inside a <Link>.
 */
function notifHref(n: NotificationItem): string {
  switch (n.kind) {
    case 'awaiting-signature':
      // The per-trust resolution detail (the signing surface lives under it).
      return `/app/trusts/${n.trustId}/resolutions/${n.entityId}`;
    case 'awaiting-acknowledgement':
      return `/app/trusts/${n.trustId}/distributions`;
    case 'upcoming-meeting':
      return `/app/trusts/${n.trustId}/meetings`;
    case 'document-rejected':
      return `/app/trusts/${n.trustId}/documents`;
    case 'unread-message':
      // The inbox is cross-trust; the thread opens from the list.
      return '/app/inbox';
    case 'member-verification-pending':
      // The portal "People & access" tab — where a trustee-tier member can see
      // + action the pending FICA/identity verification. This same switch
      // serves both the firm/admin and portal shells (there is only one
      // ScreenShell); the firm-side equivalent (/admin/fica) is a separate
      // surface for the firm's own pipeline, not this per-trust bell item.
      return `/app/trusts/${n.trustId}/people`;
    default:
      return `/app/trusts/${n.trustId}`;
  }
}

/** Compact relative-time label ("2h ago" / "in 3d") from an ISO string. */
function relativeTime(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffMs = t - now;
  const future = diffMs > 0;
  const abs = Math.abs(diffMs);
  const min = Math.round(abs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return future ? `in ${min}m` : `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return future ? `in ${hr}h` : `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return future ? `in ${day}d` : `${day}d ago`;
  const mo = Math.round(day / 30);
  return future ? `in ${mo}mo` : `${mo}mo ago`;
}

/** Detect the active trust id from a pathname (`/app/trusts/$id/...`). */
function activeTrustIdFromPath(pathname: string): string | undefined {
  const m = pathname.match(/^\/app\/trusts\/([^/]+)/);
  return m ? m[1] : undefined;
}

// ── Shared modal bodies (consumed by ScreenShell + the Dashboard) ─────────────

/**
 * SearchModalBody — TRUSTEE-11 / XCUT-06. Self-contained real search body: it
 * detects the active trust from the URL and runs the per-trust subjects search
 * (beneficiary + beneficial-owner registers). No coming-soon stub — when there
 * is no active trust it explains, honestly, that search runs within one trust.
 *
 * Exported so the Dashboard (which has its own topbar) renders the SAME body,
 * keeping the two search surfaces identical and honest.
 */
export function SearchModalBody({
  open,
  query,
  onQueryChange,
  onClose,
}: {
  open: boolean;
  query: string;
  onQueryChange: (q: string) => void;
  onClose: () => void;
}) {
  const location = useLocation();
  const activeTrustId = activeTrustIdFromPath(location.pathname);
  const subjectsSearch = useTrustSubjectsSearch({
    trustId: activeTrustId ?? '',
    query,
    enabled: open && !!activeTrustId,
  });

  return (
    <div style={{ paddingBottom: 8 }}>
      <input
        autoFocus
        type="search"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        disabled={!activeTrustId}
        placeholder={
          activeTrustId
            ? 'Search beneficiaries & beneficial owners by name…'
            : 'Open a trust to search its people'
        }
        aria-label="Search people in this trust"
        style={{
          width: '100%', padding: '10px 14px', borderRadius: 9,
          border: '1px solid var(--hairline-2)', background: 'var(--bg)',
          color: 'var(--ink)', fontFamily: 'var(--sans)', fontSize: 14,
          outline: 'none', boxSizing: 'border-box',
          opacity: activeTrustId ? 1 : 0.6,
        }}
      />
      {!activeTrustId ? (
        <div style={{ marginTop: 18, color: 'var(--ink-3)', fontSize: 13, lineHeight: 1.55 }}>
          Search runs within a single trust&rsquo;s registers. Open one of your
          trusts from <b>My trusts</b>, then search its beneficiaries and
          beneficial owners here.
        </div>
      ) : query.trim().length === 0 ? (
        <div style={{ marginTop: 18, color: 'var(--ink-3)', fontSize: 13 }}>
          Type a name to search this trust&rsquo;s beneficiaries and beneficial
          owners.
        </div>
      ) : subjectsSearch.isDebouncing || subjectsSearch.isFetching ? (
        <div style={{ marginTop: 18, color: 'var(--ink-3)', fontSize: 13 }} aria-live="polite">
          Searching&hellip;
        </div>
      ) : subjectsSearch.isError ? (
        <div style={{ marginTop: 18, color: 'var(--negative)', fontSize: 13, lineHeight: 1.55 }}>
          Couldn&rsquo;t run the search just now. Your data is safe. Please try
          again in a moment.
        </div>
      ) : subjectsSearch.subjects.length === 0 ? (
        <div style={{ marginTop: 18, color: 'var(--ink-3)', fontSize: 13 }}>
          No people match &ldquo;{query.trim()}&rdquo; in this trust.
        </div>
      ) : (
        <div role="list" style={{ marginTop: 12, display: 'flex', flexDirection: 'column' }}>
          {subjectsSearch.subjects.map((s) => (
            <Link
              key={`${s.source}:${s.recordId}`}
              role="listitem"
              // Dynamic trust path — cast as never to match the Sidebar's
              // established pattern for per-trust links.
              to={
                (s.source === 'beneficial-owner'
                  ? `/app/trusts/${activeTrustId}/beneficial-ownership`
                  : `/app/trusts/${activeTrustId}/beneficiaries`) as never
              }
              onClick={onClose}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 12, padding: '10px 4px', textDecoration: 'none',
                color: 'var(--ink)', borderBottom: '1px solid var(--hairline)',
              }}
            >
              <span>
                <span style={{ fontSize: 13.5, fontWeight: 500 }}>{displayPersonName(s.name)}</span>
                {s.maskedId && (
                  <span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)', marginLeft: 8 }}>
                    {s.maskedId}
                  </span>
                )}
              </span>
              <span className="activity-pill" style={{ fontSize: 11 }}>
                {s.source === 'beneficial-owner' ? 'Beneficial owner' : 'Beneficiary'}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/** The query-result shape the notifications body needs — a structural subset of
 *  the `useNotifications` TanStack result so callers can pass their own hook
 *  instance (scoped or cross-trust). */
interface NotificationsQueryLike {
  data?: { notifications: NotificationItem[]; unreadCount: number } | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * NotificationsModalBody — NOTIF-01. Renders the REAL pending-action feed with
 * honest loading / error / empty states and a deep link per row. Takes the
 * query result as a prop so the caller controls scoping (ScreenShell scopes to
 * the active trust; the Dashboard is cross-trust).
 */
/**
 * ToastHistorySection — what just happened in this browser.
 *
 * Paul, 25 August: "those [toasts] that appear at the bottom right — it should
 * be on the notification bell, and then I should be able to click on it and view
 * all of the notifications or dismiss it. Otherwise this bell is unnecessary."
 *
 * Separate from the server queue above it on purpose. The queue is work the
 * tenant owes; this is a second chance to read something that has already gone
 * past. Failures lead, because a failure is the one a reader came back for.
 */
function ToastHistorySection({
  records,
  onDismiss,
}: {
  records: ReadonlyArray<ToastRecord>;
  onDismiss: (id: string) => void;
}) {
  if (records.length === 0) return null;
  const failed = (k: ToastRecord['kind']): boolean => k === 'error' || k === 'warn';
  const ordered = [...records].sort((a, b) => Number(failed(b.kind)) - Number(failed(a.kind)));

  return (
    <div data-testid="notif-toast-history" style={{ marginTop: 4 }}>
      <div
        className="nav-section-label"
        style={{ padding: '10px 0 8px', borderTop: '1px solid var(--hairline)' }}
      >
        On this device
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {ordered.map((r) => (
          <li
            key={r.id}
            data-testid={`notif-toast-${r.id}`}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              padding: '9px 11px', borderRadius: 8,
              border: '1px solid var(--hairline)',
              background: failed(r.kind) ? 'var(--negative-soft)' : 'var(--bg)',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'var(--sans)', fontSize: 13, lineHeight: 1.5, color: 'var(--ink)' }}>
                {r.message}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 3, alignItems: 'baseline' }}>
                <span style={{ fontFamily: 'var(--sans)', fontSize: 11.5, color: 'var(--ink-4)' }}>
                  {relativeTime(new Date(r.at).toISOString())}
                </span>
                {r.ref && (
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-4)' }}>{r.ref}</span>
                )}
              </div>
            </div>
            <button
              type="button"
              data-testid={`notif-toast-dismiss-${r.id}`}
              onClick={() => onDismiss(r.id)}
              aria-label="Dismiss this message"
              style={{
                background: 'transparent', border: 0, padding: 0, cursor: 'pointer',
                fontFamily: 'var(--sans)', fontSize: 11.5, fontWeight: 600,
                color: 'var(--ink-3)', textDecoration: 'underline', textUnderlineOffset: 3,
              }}
            >
              Dismiss
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function NotificationsModalBody({
  query,
  onNavigate,
}: {
  query: NotificationsQueryLike;
  onNavigate: () => void;
}) {
  // A notification is a LINK. An unread-message row resolves to /app/inbox,
  // which the /app guard refuses to a firm acting as a client, so offering it
  // here is offering a dead end into the client's private correspondence. The
  // filter is by RESOLVED HREF, not by kind, so a kind added later that deep
  // links into one of the client's own screens is covered on the day it ships.
  // Everything downstream (the unread count, the filter chips, the empty
  // states) reads this list, so the count matches what is on screen.
  const hiddenFromActingFirm = useHiddenFromActingFirm();
  const notifs = (query.data?.notifications ?? []).filter(
    (n) => !hiddenFromActingFirm(notifHref(n)),
  );
  // Feature #12 + B1: read/unread filter + bulk mark-all-read. An item is read
  // when the SERVER says so (`read` — derived from the caller's mark-all-read
  // cursor, which survives a fresh session) OR the local per-item "seen"
  // overlay does (the user opened that row in this browser).
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const readIds = useNotificationsReadState();
  const markAllRead = useMarkNotificationsRead();
  const isReadItem = (n: NotificationItem): boolean => n.read || readIds.has(n.id);

  const unreadCount = notifs.reduce((n, item) => (isReadItem(item) ? n : n + 1), 0);
  const visible = filter === 'unread' ? notifs.filter((n) => !isReadItem(n)) : notifs;

  // ── WHAT JUST HAPPENED IN THIS BROWSER ─────────────────────────────────────
  //
  // Paul, 25 August: "those [toasts] that appear at the bottom right — it should
  // be on the notification bell, and then I should be able to click on it and
  // view all of the notifications or dismiss it. Otherwise this bell is
  // unnecessary."
  //
  // Kept under its OWN heading rather than merged into the queue above. They are
  // two different kinds of fact: the queue is work this tenant owes, recorded on
  // the server; this is what happened in this tab. Blending them would make a
  // local UI event look like a server record, which is the class of lie this
  // product spends most of its effort not telling.
  const toastHistory = useToastStore((t) => t.history);
  const dismissHistory = useToastStore((t) => t.dismissHistory);
  const markHistorySeen = useToastStore((t) => t.markHistorySeen);
  useEffect(() => { markHistorySeen(); }, [markHistorySeen]);

  if (query.isLoading) {
    return (
      <div style={{ padding: '24px 0', color: 'var(--ink-3)', fontSize: 13.5, textAlign: 'center' }} aria-live="polite">
        Loading the latest&hellip;
      </div>
    );
  }
  if (query.isError) {
    return (
      <div style={{ padding: '20px 4px', color: 'var(--negative)', fontSize: 13.5, lineHeight: 1.55 }}>
        Couldn&rsquo;t load notifications just now. Your data is safe. Please
        try again in a moment.
      </div>
    );
  }
  if (notifs.length === 0 && toastHistory.length === 0) {
    return (
      <div
        data-testid="notif-empty"
        style={{ padding: '24px 0', color: 'var(--ink-3)', fontSize: 13.5, textAlign: 'center' }}
      >
        You&rsquo;re all caught up. Nothing needs your attention.
      </div>
    );
  }

  // Nothing owed, but something happened here — show just the local list rather
  // than the "all caught up" line, which would be true of the queue and
  // misleading about the failure the reader came looking for.
  if (notifs.length === 0) {
    return <ToastHistorySection records={toastHistory} onDismiss={dismissHistory} />;
  }

  const chipStyle = (active: boolean): React.CSSProperties => ({
    padding: '4px 12px',
    borderRadius: 999,
    border: `1px solid ${active ? 'var(--primary-700)' : 'var(--hairline)'}`,
    background: active ? 'var(--primary-700)' : 'var(--bg)',
    color: active ? 'var(--surface)' : 'var(--ink-2)',
    fontFamily: 'var(--sans)',
    fontSize: 12.5,
    fontWeight: active ? 500 : 400,
    cursor: 'pointer',
  });

  return (
    <div>
      {/* Feature #12: filter chips + Mark all read. */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap', marginBottom: 10,
          paddingBottom: 10, borderBottom: '1px solid var(--hairline)',
        }}
      >
        <div role="group" aria-label="Filter notifications" style={{ display: 'flex', gap: 6 }}>
          <button type="button" data-testid="notif-filter-all" aria-pressed={filter === 'all'} onClick={() => setFilter('all')} style={chipStyle(filter === 'all')}>
            All
          </button>
          <button type="button" data-testid="notif-filter-unread" aria-pressed={filter === 'unread'} onClick={() => setFilter('unread')} style={chipStyle(filter === 'unread')}>
            Unread{unreadCount > 0 ? ` (${unreadCount})` : ''}
          </button>
        </div>
        <button
          type="button"
          data-testid="notif-mark-all-read"
          // B1: a REAL server write (POST /v1/notifications/read — mark-all
          // cursor). Optimistic in the hook; reconciles on refetch.
          onClick={() => markAllRead.mutate()}
          disabled={unreadCount === 0 || markAllRead.isPending}
          style={{
            background: 'none', border: 'none', padding: 0,
            color: unreadCount === 0 ? 'var(--ink-4)' : 'var(--primary-700)',
            cursor: unreadCount === 0 || markAllRead.isPending ? 'default' : 'pointer',
            fontSize: 12.5, fontFamily: 'var(--sans)',
            textDecoration: 'underline', textUnderlineOffset: 2,
          }}
        >
          {markAllRead.isPending ? 'Marking…' : 'Mark all read'}
        </button>
      </div>

      {markAllRead.isError && (
        <div role="alert" style={{ fontSize: 12.5, color: 'var(--negative)', marginBottom: 10 }}>
          Couldn&rsquo;t mark everything read just now. Please try again.
        </div>
      )}

      {visible.length === 0 ? (
        <div
          data-testid="notif-unread-empty"
          style={{ padding: '20px 0', color: 'var(--ink-3)', fontSize: 13.5, textAlign: 'center' }}
        >
          No unread notifications.
        </div>
      ) : (
        <div role="list" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {visible.map((n, i) => {
            const isRead = isReadItem(n);
            return (
              <Link
                key={n.id}
                role="listitem"
                data-testid={`notif-row-${n.id}`}
                // Dynamic per-trust deep link — cast as never (Sidebar pattern).
                to={notifHref(n) as never}
                // Opening a notification marks it read (the user has now seen +
                // acted on it), then navigates.
                onClick={() => { markNotificationRead(n.id); onNavigate(); }}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  padding: '12px 4px', textDecoration: 'none', color: 'inherit',
                  opacity: isRead ? 0.6 : 1,
                  borderBottom: i < visible.length - 1 ? '1px solid var(--hairline)' : 'none',
                }}
              >
                <div
                  className="avatar teal-soft"
                  aria-hidden="true"
                  style={{ width: 32, height: 32, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}
                >
                  <Icon name={NOTIF_ICON[n.kind]} size={15} />
                  {!isRead && (
                    <span
                      aria-hidden="true"
                      style={{
                        position: 'absolute', top: -1, right: -1,
                        width: 8, height: 8, borderRadius: 999,
                        background: 'var(--primary-700)', border: '1.5px solid var(--surface)',
                      }}
                    />
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, color: 'var(--ink)', lineHeight: 1.45, fontWeight: isRead ? 400 : 500 }}>{n.title}</div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{relativeTime(n.at)}</span>
                    {n.trustName && (
                      <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>· {displayLegalName(n.trustName)}</span>
                    )}
                    <span className="activity-pill" style={{ fontSize: 11 }}>{NOTIF_PILL[n.kind]}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
      <ToastHistorySection records={toastHistory} onDismiss={dismissHistory} />
    </div>
  );
}

// ── Cross-trust nav ───────────────────────────────────────────────────────────

interface CrossNavItem {
  icon: IconName;
  /** i18n key for the label (#42) — resolved with `t()` at render time. */
  labelKey: TranslationKey;
  key: string;
  to: string;
  badge?: number;
  /** Belongs to the client alone. Declarative only: the filter asks
   *  `useHiddenFromActingFirm` about `to`, and CI checks this flag against the
   *  guard's list. See CLIENT_ONLY_NAV_PATHS below. */
  clientOnly?: boolean;
}

// 2026-05-17 (F4d polish): the cross-trust Inbox nav item carried a hard-
// coded `badge: 3` value that didn't reflect any real unread state. The
// inbox v2 backend surface (`GET /v1/inbox/threads`) is trust-scoped, so
// a cross-trust unread aggregator endpoint does not yet exist. Per the
// re-audit rule "if endpoint doesn't support this yet — render no badge
// instead of fake '3'", the badge is intentionally absent here. When the
// aggregator endpoint lands (tracked under Phase 4 IA migrations) wire a
// `useInboxUnreadCount()` hook into a per-item override below.
const CROSS_NAV: CrossNavItem[] = [
  { icon: 'overview', labelKey: 'nav.overview', key: 'overview', to: '/app/dashboard' },
  { icon: 'trust',    labelKey: 'nav.myTrusts', key: 'trusts',   to: '/app/trusts' },
  { icon: 'msg',      labelKey: 'nav.inbox',    key: 'inbox',    to: '/app/inbox',    clientOnly: true },
  { icon: 'shield',   labelKey: 'nav.security', key: 'security', to: '/app/security', clientOnly: true },
  { icon: 'key',      labelKey: 'nav.firmAccess', key: 'firmAccess', to: '/app/firm-access', clientOnly: true },
  { icon: 'lock',     labelKey: 'nav.privacy',  key: 'privacy',  to: '/app/privacy',  clientOnly: true },
];

/**
 * ── THE CLIENT'S OWN FOUR SCREENS ───────────────────────────────────────────
 *
 * Four of the six Account destinations belong to the CLIENT and to nobody else.
 * A firm administering that client must not reach them.
 *
 *   Inbox        — the client's private correspondence.
 *   Security     — their password, their authenticator, their sessions.
 *   Firm access  — the page on which they grant and REVOKE this very firm.
 *   Privacy      — the scopes that decide what this very firm may see.
 *
 * Asked three times. Beno, walking as the firm on 22 August, opened Firm access
 * and revoked Klaprops' own access. Paul, 25 August 14:24: *"I shouldn't be able
 * to access their inbox. And I shouldn't be able to access a firm access or
 * privacy."* Paul again, 25 August 19:31: *"I actually asked AI to remove the
 * fact that you as an administrator can access the privacy, can see security and
 * inbox. I just would want them to see the trusts and the overview."*
 *
 * A frame of that same recording at 26:40 shows all four still in the sidebar
 * with the act-as banner reading "Administering Beno · Managing", and a frame at
 * 19:20 of the 14:24 recording shows the Firm access page fully open under
 * act-as with working Grant and Revoke controls.
 *
 * The last two clauses of Paul's sentence are the whole specification: **the
 * trusts and the overview**, nothing else.
 *
 * HIDING THE LINK IS NOT THE FIX. It is half of it. This filter stops the
 * accident; `isClientOnlyPath` in `lib/authz/route-policy.ts` stops the typed
 * URL, the stale bookmark and the back button; the server-side privacy scopes
 * shipped on 23 August stop the fetch.
 *
 * `route-policy.ts` owns the authoritative list, and since 2026-08-27 the
 * RUNTIME reads it directly: the filter above asks `useHiddenFromActingFirm`
 * about each item's own `to` path, so the sidebar cannot hide a different set
 * from the one the guard blocks even for one render. The `clientOnly` flag is
 * now a DECLARATION rather than a decision, kept because reading this array
 * should tell you which rows belong to the client, and `client-only-paths.test.ts`
 * fails if the declaration and the guard's list ever disagree. A nav that hides
 * five while the guard blocks four is how a hole opens quietly.
 */
export const CLIENT_ONLY_NAV_PATHS: readonly string[] = CROSS_NAV
  .filter((it) => it.clientOnly)
  .map((it) => it.to);

// ── THE PER-TRUST NAV LIVES IN ONE PLACE NOW (TOV-01 · NAV-04, 2026-08-27) ───
//
// This file used to carry a `TRUST_NAV` array of fifteen destinations and render
// it down the sidebar whenever the URL was inside /app/trusts/$id/*. The trust
// TAB STRIP (`components/screens/trust-tabs.tsx`) carries the same fifteen, and
// a frame of the 25 August walkthrough at 26:40 shows both on screen at once.
// Paul: "it's two of the same things and it's taking up space."
//
// Three people wanted three things on that call. Beno wanted the top strip kept
// and frozen like Excel freeze panes. Waldo wanted the left one kept so he never
// had to scroll back up to navigate. Paul wanted the duplication gone and warned
// that a frozen bar costs about an inch of working height. The strip is now
// sticky and a single compact row, which answers Waldo's objection as well as
// Beno's: it does not scroll away, so there is nothing to scroll back up to.
//
// WHAT WAS LOST WITH THE LIST, and must not be lost again. Paul, walking prod on
// 25 August: "I don't know how to add my own assets... there's no asset option
// here under Lubabalo trust. So I think there might be a few things missing here
// on the left menu." He was right and it was exactly four: Assets,
// Distributions, Tax and People & access existed and were reachable only by
// noticing the horizontally-scrolling strip. They were added here the same day.
// The strip must therefore keep ALL fifteen, which it does, and stay visible,
// which the sticky positioning is for. If the strip is ever narrowed, this
// discoverability problem comes back and the sidebar list is not the answer to
// it a second time.

// ── Account menu (nav-foot) ───────────────────────────────────────────────────

/**
 * AccountMenu — TRUSTEE-05 follow-up (Paul: "where is the logout??").
 *
 * The profile identity block (avatar + name + role) is now a BUTTON that opens
 * a small popover anchored at the nav-foot, carrying the two account actions:
 *   • View profile → navigates to /app/profile
 *   • Sign out     → calls the shared handleSignOut() (Supabase signOut + redirect)
 *
 * This replaces the easy-to-miss icon-only sign-out. Mirrors the established
 * dropdown idiom in `header/tenant-switcher.tsx`: outside-click + Escape close,
 * `aria-haspopup="menu"` / `aria-expanded`, `role="menu"` / `role="menuitem"`,
 * and a chevron that signals the trigger opens a menu.
 */
function AccountMenu({
  displayName,
  email,
  roleLabel,
  onViewProfile,
  onSignOut,
}: {
  displayName: string;
  email: string;
  roleLabel: string;
  /** Navigate to the profile screen (also runs the shell's onNavigate). */
  onViewProfile: () => void;
  /** Sign out + redirect (the Sidebar's handleSignOut). */
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  // Close on outside click or Escape (same pattern as the tenant switcher).
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const itemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    textAlign: 'left',
    padding: '9px 11px',
    borderRadius: 9,
    border: 'none',
    background: 'transparent',
    color: 'var(--ink)',
    fontFamily: 'var(--sans)',
    fontSize: 13.5,
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'background 120ms var(--ease-cushion)',
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <button
        type="button"
        className="nav-foot-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label="Open account menu"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          width: '100%', minWidth: 0,
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'inherit', textAlign: 'left', font: 'inherit',
        }}
      >
        <div className="avatar teal-soft" aria-hidden="true">
          {displayName ? initialsOf(displayName) : '·'}
        </div>
        <div className="nav-foot-name" style={{ minWidth: 0, flex: 1 }}>
          {/* THE NAME WRAPS RATHER THAN CLIPS (NAV-03).
              A frame of the 25 August walkthrough shows this rail reading
              "EMMANUEL PA..." over "Founder & Trus...". The 23 August pass added
              an ellipsis to the ROLE line and left the NAME clipping, and after
              ID verification the display name becomes the full name off the ID
              document, so it is longer than the rail from that moment on.

              A 26 August pass then put a two-line clamp INLINE here, which
              changed nothing on screen: the inline style overrode `display` and
              `overflow` but not `white-space: nowrap`, still in force from
              `.nav-foot-name b` in design-system.css, and with nowrap there are
              no soft-wrap opportunities for a clamp to use. The clamp now lives
              in that rule instead, so this shell, the firm console and the
              superadmin console all get it from one place. Nothing inline here.

              The full string stays on `title`, and the account menu this block
              opens always shows it in full.

              NOT DONE, and it needs Paul: the approved mock showed a short
              "goes by" name over the full legal one. We do not store a preferred
              name and it cannot be derived. Emmanuel Paul Prinsloo goes by his
              SECOND given name, so first-plus-surname would confidently produce
              the wrong one. Guessing which of a person's names they use is
              exactly the class of invention this product refuses elsewhere. A
              real preferred-name field is a small backend change and his call.

              TYP-02: the name is cased at RENDER by the one shared rule, so an
              all-capitals value off an identity document stops shouting on this
              screen while reading in ordinary case on the next one. */}
          <b title={displayName || email}>{displayPersonName(displayName) || email}</b>
          <span title={roleLabel}>{roleLabel}</span>
        </div>
        {/* Chevron signals the identity block opens a menu. */}
        <svg
          width={14}
          height={14}
          viewBox="0 0 20 20"
          aria-hidden="true"
          style={{
            flexShrink: 0,
            color: 'var(--ink-3)',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 160ms var(--ease-cushion)',
          }}
        >
          <path
            d="M5 8l5 5 5-5"
            stroke="currentColor"
            fill="none"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Account"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            minWidth: 200,
            padding: 6,
            background: 'var(--bg)',
            border: '1px solid var(--hairline-2)',
            borderRadius: 12,
            boxShadow:
              '0 12px 32px rgba(20, 18, 12, 0.08), 0 2px 4px rgba(20, 18, 12, 0.04)',
            zIndex: 50,
          }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onViewProfile(); }}
            style={itemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <Icon name="overview" size={16} />
            <span>View profile</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="nav-foot-signout"
            onClick={() => { setOpen(false); onSignOut(); }}
            style={itemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <Icon name="external" size={16} />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ── Sidebar ──────────────────────────────────────────────────────────────────

export function Sidebar({
  activeNav,
  open,
  onNavigate,
}: {
  activeNav?: string;
  open?: boolean;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  // i18n (#42): the cross-trust nav labels are part of the wired slice.
  const { t } = useTranslation();

  // TRUSTEE-05: a visible sign-out in the nav-foot. The only prior sign-out was
  // buried in the Security Centre; this surfaces it everywhere the shell shows.
  async function handleSignOut() {
    await signOutAndRedirect(navigate);
  }

  // Is this destination hidden from whoever is at the keyboard right now? The
  // shared gate (lib/authz/act-as-nav) reads the act-as posture and the route
  // guard's own CLIENT_ONLY_PATHS, so the sidebar, the dashboard hero, the
  // profile screen and the notification bell all ask one question and get one
  // answer. See the constant below for the ruling and the three times it was
  // asked.
  const hiddenFromActingFirm = useHiddenFromActingFirm();

  // NAV-02: this sidebar has its own scrollbar and the shell remounts on every
  // navigation, so without this the menu snapped back to the top each time. The
  // logic moved into a shared hook on 27 August because the same defect was
  // still live on the firm console, which never got the inline version.
  const sidebarScroll = useSidebarScrollMemory<HTMLElement>('client-sidebar');

  // Detect whether we are inside a /app/trusts/$id/* route and extract the trustId.
  const trustMatch = location.pathname.match(/^\/app\/trusts\/([^/]+)/);
  const activeTrustId = trustMatch ? trustMatch[1] : null;

  // Real data hooks — replace the hardcoded persona that leaked here pre-Phase 3
  // residual cleanup. `useTrust` is gated on `activeTrustId` so it doesn't fire
  // on cross-trust screens. S-1.1: the nav-foot identity is the OPERATOR (the
  // logged-in human), never the act-as client — so it reads `useOperatorMe`, not
  // the act-as-following `useMe`. Otherwise a firm administering a client would
  // see the CLIENT's name/email/role in their own account menu (the identity bleed).
  const trustQuery = useTrust(activeTrustId ?? undefined);
  const meQuery = useOperatorMe();
  const trust = trustQuery.data?.trust;
  // The caller's portal tier on THIS trust ('trustee' | 'beneficiary'), or
  // null/undefined when not a member (tenant-wide reader). Lives alongside
  // `trust` on the same TrustGetResponse — NOT a field on `trust` itself.
  const callerTier = trustQuery.data?.callerTier;
  const me = meQuery.data?.user;

  // FE-8 (Henry R2-009): a self-onboarded direct (b2c) settlor carries the
  // Supabase `admin` role (they administer their own synthetic tenant), so the
  // nav-foot label read "Administrator" on cross-trust screens even though it
  // correctly showed "Settlor & Trustee" inside one of their trusts. There is
  // no `kind:'b2c'` signal on the client today — neither `useMe` nor
  // `useTenantMe` (GET /v1/tenants/me) returns the tenant kind. Rather than
  // fabricate one, we reuse the SAME predicate the in-trust label already
  // trusts (memberIds[0] === me.supabaseUserId — the onboard handler always
  // writes the settlor first) and apply it across the user's whole trust list.
  // A user who is the settlor (first member) of any of their trusts is a
  // settlor-trustee regardless of b2b/b2c; a firm admin who is a member of no
  // trust keeps "Administrator". The list query already populates the "My
  // trusts" cache, so this is a free read on most screens.
  // CAREFUL: `memberIds` is now OPTIONAL on the wire: the API omits it for a caller
  // who may not read a trust's `people` surface (a beneficiary-tier member).
  // Absence resolves to "not the founder", which is the right answer — a
  // beneficiary is not the settlor — but it MUST be reached by optional access,
  // not by indexing into undefined.
  const trustsQuery = useTrusts();
  const isSettlorOfAnyTrust = !!me?.supabaseUserId
    && (trustsQuery.data?.trusts ?? []).some(
      (t) => t.memberIds?.[0] === me.supabaseUserId,
    );

  // White-label: swap the sidebar wordmark + logo to the active firm branding
  // — the host subdomain OR (on bare apex) the logged-in user's tenant. Reads
  // the TanStack cache populated at the route root, so it's a free read.
  const branding = useActiveBranding();
  // CLB-08 + NEW-actas-identity-label (grader, 5 Sep 2026). Under act-as this
  // shell is the FIRM's surface in front of its client. The firm's name goes
  // top-left even when it has published no branding (its own tenant name, the
  // same fallback the admin console uses), and the operator is labelled by their
  // OWN role and the client they are acting for, never by a chair on the
  // client's trust: "Founder & Trustee" was the firm's admin reading the seat
  // the walk had put them in as the trust's first member.
  const acting = useActingContext();
  const actingForClient = acting.isStandingIn && acting.subject.kind === 'client';
  const operatorFirmQ = useTenantMe({ enabled: actingForClient, useOperatorSession: true });
  const operatorFirmName = actingForClient
    ? (operatorFirmQ.data?.branding?.firmName ?? operatorFirmQ.data?.display_name ?? '')
    : '';
  // firm-walkthrough 2026-07-20 (P2): the firm name showed twice (inside the
  // logo AND as a mid-word-clipped wordmark beside it). A logo carries the
  // identity, so the text wordmark shows only when there is no logo.
  const lockup = resolveBrandLockup({
    hasLogo: !!branding?.logoUrl,
    brandingFirmName: branding?.firmName || operatorFirmName || null,
  });
  // BRD-05 (Paul, M1 §H): a firm with no logo shows the FIRM'S NAME, never the
  // Elreign "E" — this sidebar is the white-label surface a firm's client sees.
  // Only the PUBLISHED firm name qualifies here: a self-managed individual's
  // tenant is named after a person, and a person's initials are not a brand,
  // so with no firm identity the Elreign mark stays (the admin console, a
  // firm-only surface, can fall back further — see admin-layout.tsx).
  const firmMarkName = (branding?.firmName || operatorFirmName || '').trim();
  const noLogoMark = firmMarkName ? <FirmMonogram name={firmMarkName} size={28} /> : <ReignMark size={28} />;

  return (
    /* `role="navigation"` (2026-08-22 a11y sweep): an <aside> is a
       COMPLEMENTARY landmark, so the app's primary navigation was announced as
       "complementary" and there was no navigation landmark anywhere in the
       authenticated product. The explicit role overrides the implicit one;
       `aria-label` names it, which is what lets a screen-reader user jump
       straight to it. The element stays an <aside> so every `.nav` /
       `aside.nav` style and the mobile drawer transform are untouched. */
    <aside
      ref={sidebarScroll.ref}
      onScroll={sidebarScroll.onScroll}
      className={'nav' + (open ? ' open' : '')}
      role="navigation"
      aria-label="Primary"
    >
      <div className="brand-row">
        {branding?.logoUrl ? (
          <FirmLogo
            src={branding.logoUrl}
            alt={branding.firmName ? `${branding.firmName} logo` : 'Firm logo'}
            // A broken firm logo URL (COMBO-001) falls back to the same
            // no-logo answer (the firm's monogram, BRD-05) rather than a
            // broken-image glyph in the sidebar.
            fallback={noLogoMark}
            // Fixed height matches the mark footprint; width is free (capped)
            // so a wide wordmark logo keeps its aspect instead of being squished
            // into a 28px square.
            style={{ height: 28, width: 'auto', maxWidth: 140, objectFit: 'contain', display: 'block' }}
          />
        ) : (
          noLogoMark
        )}
        {lockup.showWordmark && <div className="wordmark">{lockup.wordmark}</div>}
      </div>

      {/* Cross-trust section */}
      <div>
        <div className="nav-section-label">Account</div>
        <div className="nav-list">
          {CROSS_NAV.filter((it) => !hiddenFromActingFirm(it.to)).map((it) => {
            // ── ONE ITEM LIT AT A TIME (2026-08-26) ──────────────────────────
            //
            // Paul, 25 August: "if I click on beneficiaries, Overview AND
            // Beneficiaries is highlighted. And if I go down the list, Overview
            // and Beneficiaries is highlighted. Portfolio and all of that still
            // highlighted."
            //
            // The prefix match was the cause. "My trusts" points at
            // /app/trusts, so on /app/trusts/{id}/anything it matched
            // `startsWith('/app/trusts/')` and stayed lit for every screen
            // inside every trust — while the per-trust section below ALSO lit
            // the tab you were actually on. Two lit items, always, once you were
            // inside a trust.
            //
            // The prefix rule is still right for a section that owns its
            // subtree, so it is kept and narrowed: an item stands down when a
            // MORE SPECIFIC answer to "where am I" is on screen beside it.
            //
            // Until 2026-08-27 that more specific answer was the per-trust
            // section rendered directly below this list, so `/app/trusts` stood
            // down inside a trust — by URL AND by the `activeNav` override,
            // which the screens set to "trusts" for their own reasons
            // (people-screen.tsx does) and which was lighting the parent no
            // matter what the URL said. Both routes to "lit" had to be closed or
            // the other one simply won.
            //
            // That section is gone (TOV-01: the trust sections were rendering
            // twice, here and in the tab strip). Nothing in the sidebar is more
            // specific any more, so standing down would leave the whole sidebar
            // dark while the user is unmistakably inside a trust, which is the
            // opposite failure to the one NAV-01 fixed. "My trusts" is now the
            // one lit item inside a trust, and the tab strip says which section.
            // e2e/96 NAV-01 asks for exactly one, and gets exactly one.
            const isActive = activeNav
              ? it.key === activeNav
              : location.pathname === it.to || location.pathname.startsWith(it.to + '/');
            return (
              <Link
                key={it.key}
                to={it.to as never}
                // ── THE HIGHLIGHT WAS NEVER OURS TO SET (2026-08-26) ──────────
                //
                // Paul, 25 August: "if I click on beneficiaries, Overview AND
                // Beneficiaries is highlighted... Portfolio and all of that
                // still highlighted."
                //
                // `isActive` below looks like it decides this. It does not.
                // TanStack Router's <Link> adds `className: 'active'` AND
                // `aria-current="page"` on its own, and its default matching is
                // FUZZY — every ancestor path counts as active. So inside
                // /app/trusts/{id}/people the router lit "My trusts", the
                // trust's "Overview" and "People & access" together, and no
                // amount of editing `isActive` could change it. Proven by
                // e2e/96: three items carried aria-current with our own rule
                // returning false for two of them.
                //
                // `exact` puts the router's notion back under our control. The
                // per-trust list below opts INTO prefix matching for exactly the
                // entries that own a subtree.
                activeOptions={{ exact: true }}
                className={'nav-item' + (isActive ? ' active' : '')}
                aria-current={isActive ? 'page' : undefined}
                onClick={onNavigate}
              >
                <Icon name={it.icon} size={17} />
                <span>{t(it.labelKey)}</span>
                {it.badge !== undefined && (
                  <span className="nav-badge">{it.badge}</span>
                )}
              </Link>
            );
          })}
        </div>
      </div>

      <div className="nav-foot">
        {me ? (
          // TRUSTEE-05 follow-up: the identity block is now a menu trigger
          // (avatar + name + chevron) that opens an account popover with
          // "View profile" + "Sign out" — making logout discoverable. The
          // role label keeps the H-81 + FE-8 SA-trust-aware semantics: show
          // "Settlor & Trustee" when EITHER (a) we're inside a trust page and
          // the user is its first member (the settlor — the onboard handler
          // always puts the creating user first), OR (b) cross-trust, the user
          // is the settlor of any of their own trusts (the b2c direct case,
          // which would otherwise mislabel as "Administrator"). That special
          // case wins outright. Otherwise, inside a trust context where the
          // TrustGetResponse carries a `callerTier` for this member (C5:
          // multi-member end-client access — a portal member's tier can be
          // 'trustee' or 'beneficiary', and every role-'user' portal member
          // was previously shown the generic "Trustee" label regardless of
          // their actual tier), show the tier-specific label. Cross-trust (no
          // active trust) or no resolvable tier falls back to the centralised
          // labelForRole mapping (role 'user' now reads 'Member').
          <AccountMenu
            displayName={me.displayName ?? ''}
            email={me.email}
            roleLabel={
              actingForClient
                ? `${labelForRole(acting.operator.role)} · acting for ${displayPersonName(acting.subject.displayName) || 'this client'}`
                : (trust && me.supabaseUserId && trust.memberIds?.[0] === me.supabaseUserId)
                || isSettlorOfAnyTrust
                // Vocabulary (Beno, 2026-07-30): an SA trust deed names a
                // FOUNDER, not a settlor. Same role, the word a client reads.
                ? 'Founder & Trustee'
                : trust && callerTier === 'beneficiary'
                  ? 'Beneficiary'
                  : trust && callerTier === 'trustee'
                    ? 'Trustee'
                    : labelForRole(me.role)
            }
            onViewProfile={() => {
              onNavigate?.();
              void navigate({ to: '/app/profile' });
            }}
            onSignOut={() => void handleSignOut()}
          />
        ) : (
          <>
            <div className="avatar teal-soft" aria-busy="true" aria-label="Loading user">
              &nbsp;
            </div>
            <div className="nav-foot-name" aria-busy="true">
              <b style={{ opacity: 0.5 }}>Loading…</b>
              <br />
              <span style={{ opacity: 0.5 }}>&nbsp;</span>
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

// ── ScreenShell ──────────────────────────────────────────────────────────────

interface ScreenShellProps {
  activeNav?: string;
  /** Optional since the cohesion pass folded the dashboard in: that screen
   *  shows a "Day one" eyebrow ONLY before the tenant has ever been updated,
   *  and an always-rendered empty `.eyebrow` div would leave its 8px bottom
   *  margin behind on every other visit. */
  eyebrow?: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  children: ReactNode;
  topbarRight?: ReactNode;
}

export function ScreenShell({
  activeNav,
  eyebrow,
  title,
  sub,
  children,
  topbarRight,
}: ScreenShellProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // F-024, finally connected (2026-08-06). Feeds the tenant store from
  // /v1/me/tenants so the switcher in the topbar has something to render. It
  // self-hides for anyone with one workspace, which is everybody until the
  // membership backfill runs — so this is invisible today and correct tomorrow.
  useSyncMyTenants();

  // POR-05 — light / dark for the whole portal, not just its dashboard. Writes
  // `data-theme` on <html>, so the sidebar, the modals and the toast layer
  // re-skin with it.
  const { theme, toggleTheme } = useAdminTheme();

  // Active-trust detection (mirrors the Sidebar) — scopes both the search and
  // the notification feed to the trust the user is currently inside; cross-trust
  // screens fall back to all visible trusts.
  const activeTrustId = useMemo(() => {
    const m = location.pathname.match(/^\/app\/trusts\/([^/]+)/);
    return m ? m[1] : undefined;
  }, [location.pathname]);

  // ADMIN-13 follow-up: when entered from the admin console (`?from=admin`),
  // surface a slim "Back to admin" banner so the operator can return. `from` is
  // an un-typed search param (not in any route's search schema), so we parse it
  // off the router's raw query string (`searchStr`) — which TanStack updates on
  // every navigation, so the memo stays correct and lint-clean.
  const cameFromAdmin = useMemo(() => {
    const raw = (location as { searchStr?: string }).searchStr ?? '';
    return new URLSearchParams(raw).get('from') === 'admin';
  }, [location]);

  // NOTIF-01: the REAL feed. When inside a trust, scope to it; otherwise the
  // caller's whole visible set. Never fabricated — an empty array is the honest
  // "all caught up" state rendered by NotificationsModalBody. The search body
  // owns its own scoped query, so only the feed is wired here.
  const notifQ = useNotifications(activeTrustId ? { trustId: activeTrustId } : {});
  const unread = notifQ.data?.unreadCount ?? 0;

  // BEL-03 / POR-04. Paul, 25 August: "the notification thing opens up the whole
  // screen here if I click on the bell. So make that also small." On every /app
  // screen this was a centred Modal, which covers the page the reader was
  // looking at to tell them there is nothing to look at. The firm console has
  // answered this correctly since 23 August and the dashboard since 26 August:
  // an anchored popover under the bell. This is the last of the three.
  //
  // The BODY is unchanged. It is still the shared NotificationsModalBody, so the
  // act-as link filtering inside it still holds and the toast history under it
  // still renders. Only the container moved.
  const notifAnchorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!notifOpen) return;
    const onPointer = (e: MouseEvent) => {
      if (!notifAnchorRef.current?.contains(e.target as Node)) setNotifOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNotifOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [notifOpen]);

  return (
    <div className="app app-warm" style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <SkipToContentLink />
      <Sidebar activeNav={activeNav} open={navOpen} onNavigate={() => setNavOpen(false)} />
      <div
        className={'nav-backdrop' + (navOpen ? ' open' : '')}
        onClick={() => setNavOpen(false)}
        aria-hidden="true"
      />
      <main id="main" tabIndex={-1}>
        {/* ADMIN-13 follow-up: slim "Back to admin" banner when the client
            portal was entered from the admin console (?from=admin). */}
        {cameFromAdmin && (
          <Link
            to="/admin"
            data-testid="back-to-admin"
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 16px', fontSize: 13, fontWeight: 500,
              color: 'var(--primary-700)', background: 'var(--primary-50, var(--surface-2))',
              borderBottom: '1px solid var(--hairline)', textDecoration: 'none',
            }}
          >
            <Icon name="arrow-right" size={14} style={{ transform: 'rotate(180deg)' }} />
            Back to admin console
          </Link>
        )}
        <div className="topbar">
          <button
            type="button"
            className="nav-hamburger"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation menu"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M3 5h14M3 10h14M3 15h14"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <div className="greeting">
            {eyebrow && <div className="eyebrow">{eyebrow}</div>}
            <h1>{title}</h1>
            {sub && <div className="sub">{sub}</div>}
          </div>
          {/*
            COMPOSITION, NOT REPLACEMENT (2026-08-08).

            This was `{topbarRight ?? (<globals/>)}` — a replacement slot. Any
            screen passing a single action button therefore DELETED every global
            control on that screen. Fifteen screens do pass one, and on eight
            trustee-facing screens (Documents, Resolutions, Meetings,
            Distributions, People, Audit, Beneficial ownership, the trust list)
            that silently removed the tenant switcher, Search, AND the
            notification bell — the product's only pending-action nudge — from
            exactly the screens a trustee works in.

            Rendering both makes that impossible by construction rather than by
            review discipline: a screen can add an action, it can no longer
            remove the globals.
          */}
          <div className="topbar-actions">
            {topbarRight}
            {/*
              F-024 tenant switcher — self-hides for single-tenant users.
              The TODO that stood here from Phase 10 until 2026-08-06 asked for
              exactly one thing: someone to fetch /api/v1/me/tenants and call
              setTenants. Nobody did, because the endpoint did not exist, so
              the switcher rendered an empty list and hid itself forever.
              Both now exist — see `useSyncMyTenants` above and
              docs-vault/10-identity/.
            */}
            <TenantSwitcher />
            <button className="icon-btn" aria-label="Search" onClick={() => setSearchOpen(true)}>
              <Icon name="search" size={18} />
            </button>
            {/* POR-05 — light / dark. Offers the theme you would switch TO, the
                same way the console does. Moved here from the dashboard's own
                topbar by the cohesion pass, which is the whole reason the other
                seventeen portal screens now have it. */}
            <button
              className="icon-btn"
              type="button"
              data-testid="portal-theme-toggle"
              aria-pressed={theme === 'dark'}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              onClick={toggleTheme}
            >
              {theme === 'dark' ? (
                <svg width="17" height="17" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.3" />
                  <path
                    d="M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15M2.64 2.64l1.06 1.06M12.3 12.3l1.06 1.06M13.36 2.64l-1.06 1.06M3.7 12.3l-1.06 1.06"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <svg width="17" height="17" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M13.5 9.6A5.8 5.8 0 0 1 6.4 2.5a5.8 5.8 0 1 0 7.1 7.1Z"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
            {/* BEL-03 — the bell and its panel share one positioned wrapper so
                the panel anchors under the button instead of over the page. */}
            <div ref={notifAnchorRef} style={{ position: 'relative' }}>
              <button
                className="icon-btn"
                type="button"
                aria-haspopup="dialog"
                aria-expanded={notifOpen}
                aria-label={unread > 0 ? `Notifications, ${unread} pending` : 'Notifications'}
                onClick={() => setNotifOpen((o) => !o)}
              >
                <Icon name="bell" size={18} />
                {/* Real unread badge, gated on the live feed's count, never a
                    hardcoded dot. */}
                {unread > 0 && <span className="dot" data-testid="notif-dot" />}
              </button>
              {notifOpen && (
                <div
                  role="dialog"
                  aria-label="Notifications"
                  data-testid="notifications-popover"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    width: 340,
                    padding: '6px 10px 10px',
                    maxHeight: 'min(70vh, 520px)',
                    overflowY: 'auto',
                    background: 'var(--bg)',
                    border: '1px solid var(--hairline-2)',
                    borderRadius: 12,
                    boxShadow:
                      '0 12px 32px rgba(20, 18, 12, 0.10), 0 2px 4px rgba(20, 18, 12, 0.04)',
                    zIndex: 60,
                    textAlign: 'left',
                  }}
                >
                  <NotificationsModalBody
                    query={notifQ}
                    onNavigate={() => setNotifOpen(false)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search modal — TRUSTEE-11 / XCUT-06: real trust-scoped people search
            via the shared SearchModalBody (no coming-soon stub). */}
        <Modal open={searchOpen} onClose={() => setSearchOpen(false)} title="Search people" size="md">
          <SearchModalBody
            open={searchOpen}
            query={searchQuery}
            onQueryChange={setSearchQuery}
            onClose={() => setSearchOpen(false)}
          />
          <Modal.Footer>
            <button className="btn-secondary" onClick={() => setSearchOpen(false)}>Close</button>
          </Modal.Footer>
        </Modal>

        {/* The notification panel is NOT here. BEL-03 moved it up beside the
            bell it belongs to, so it opens as a small anchored popover rather
            than a full-screen modal over the page the reader is on. */}

        {children}
      </main>
    </div>
  );
}
